import S from 'common/service/sanctuary';
import { CancelingDialog } from 'common/components/dialogs';
import { withConfirmationDialog } from 'common/mdc/dialog';
import { addIndex, compose, findIndex, propEq } from 'ramda';
import React from 'react';
import { func, number, element, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, routerShape } from 'found';
import { selectors, actions } from './state';
import Toolbar, { ToolbarRow, ToolbarSection } from '../toolbar';
import Step from './step';

const filterByType = (elements, type) => React.Children
  .toArray(elements)
  .filter(e => e.type === type);

class Stepper extends React.PureComponent {
  constructor(props) {
    super(props);

    this.steps = S.map(
      S.prop('props'),
      filterByType(props.children, Step),
    );

    const { router, cancelingDialog } = this.props;

    this.removeHook = router.addTransitionHook(() => new Promise((resolve) => {
      cancelingDialog({ resolve, reject: () => false });
    }));
  }

  componentWillMount() {
    const initialStepIx = findIndex(propEq('stepId', this.props.initialStep))(this.steps);

    this.props.init({
      current: this.props.initialStep,
      steps: this.steps.map((step, idx) => ({
        ...step,
        data: this.props.initialData[idx],
        completed: idx < initialStepIx,
      })),
    });
  }

  componentWillUnmount() {
    this.props.reset();
    this.removeHook();
  }

  render() {
    const {
      getStepState,
      getData,
      hasError,
      current,
      currentStep,
      back,
      cleanError,
      resolve,
      reject,
    } = this.props;

    const steps = addIndex(S.map)(
      (step, idx) => {
        const stepId = step.stepId || idx;
        const state = S.fromMaybe({}, getStepState(stepId));

        return (
          <Step
            {...step}
            index={state.index}
            key={state.index || idx}
            completed={state.completed}
            active={current === idx}
            error={hasError(stepId)}
            enabled={Boolean(state.completed || state.error)}
          />
        );
      },
      this.steps,
    );

    const content = this.steps[current].children;

    return (
      <div className="mdc-stepper">
        <Toolbar>
          <ToolbarRow>
            <ToolbarSection>
              <ul className="mdc-stepper-head">
                {steps}
              </ul>
            </ToolbarSection>
          </ToolbarRow>
        </Toolbar>
        {(
          S.maybeToNullable(S.map(() => (
            S.is(Function, content)
              ? content({
                back,
                cleanError,
                resolve,
                reject,
                getData,
                hasError,
                removeHook: this.removeHook,
              })
              : content
          ),
            currentStep))
        )}
      </div>
    );
  }
}

Stepper.propTypes = {
  init: func.isRequired,
  reset: func.isRequired,
  current: number.isRequired,
  children: arrayOf(element).isRequired,
  router: routerShape.isRequired,
};

export default compose(
  connect(
    state => ({
      current: selectors.getCurrent(state),
      getStepState: selectors.getStepState(state),
      currentStep: selectors.getCurrentStep(state),
      getData: selectors.getData(state),
      hasError: selectors.hasError(state),
    }),
    actions,
  ),
  withConfirmationDialog(CancelingDialog, 'cancelingDialog'),
  withRouter,
)(Stepper);
